import { useEffect, useState } from "react";
import { MainLayout } from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { useAdminApi, DashboardStats } from "@/hooks/useAdminApi";
import { Building2, Users, Activity, TrendingUp, TrendingDown, Loader2, User, MessageSquare, Volume2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatDistanceToNow } from "date-fns";
import { pl, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";

export default function AdminDashboard() {
  const { t, i18n } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const { getDashboardStats } = useAdminApi();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = async () => {
    setIsLoading(true);
    setError(null);
    const result = await getDashboardStats();
    if (result.success && result.data) {
      setStats(result.data);
    } else {
      setError(result.error || t('dashboard.errorLoading'));
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const getUsageTypeIcon = (type: string) => {
    switch (type) {
      case 'linkedin_profile': return <User className="h-4 w-4 text-blue-500" />;
      case 'message_generated': return <MessageSquare className="h-4 w-4 text-green-500" />;
      case 'audio_generated': return <Volume2 className="h-4 w-4 text-purple-500" />;
      default: return <Activity className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getUsageTypeName = (type: string) => {
    switch (type) {
      case 'linkedin_profile': return t('companyDashboard.usageTypes.linkedin_profile');
      case 'message_generated': return t('companyDashboard.usageTypes.message_generated');
      case 'audio_generated': return t('companyDashboard.usageTypes.audio_generated');
      default: return type;
    }
  };

  const dateLocale = i18n.language === 'pl' ? pl : enUS;

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{t('dashboard.title')}</h1>
            <p className="text-muted-foreground">
              {t('dashboard.subtitle')}
            </p>
          </div>
          <Button variant="outline" onClick={fetchStats} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            {tc('refresh')}
          </Button>
        </div>

        {error && (
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <p className="text-sm text-destructive">{error}</p>
            </CardContent>
          </Card>
        )}

        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('dashboard.companies')}</CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                <div className="text-2xl font-bold">{stats?.companies_count ?? 0}</div>
              )}
              <p className="text-xs text-muted-foreground">
                {t('dashboard.companiesDesc')}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('dashboard.users')}</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                <div className="text-2xl font-bold">{stats?.users_count ?? 0}</div>
              )}
              <p className="text-xs text-muted-foreground">
                {t('dashboard.usersDesc')}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('dashboard.usageToday')}</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                <div className="text-2xl font-bold">{stats?.today_usage ?? 0}</div>
              )}
              <p className="text-xs text-muted-foreground">
                {t('dashboard.usageTodayDesc')}
              </p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.recentActivity')}</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="flex items-center gap-4">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="space-y-2 flex-1">
                      <Skeleton className="h-4 w-48" />
                      <Skeleton className="h-3 w-32" />
                    </div>
                  </div>
                ))}
              </div>
            ) : stats?.recent_activity && stats.recent_activity.length > 0 ? (
              <div className="space-y-4">
                {stats.recent_activity.map((activity) => (
                  <div key={activity.id} className="flex items-center gap-4 py-2 border-b border-border last:border-0">
                    <div className="h-10 w-10 rounded-full bg-secondary flex items-center justify-center">
                      {getUsageTypeIcon(activity.usage_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {getUsageTypeName(activity.usage_type)}
                        </Badge>
                        <span className="text-sm font-medium truncate">
                          {activity.company_name}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(activity.created_at), { 
                          addSuffix: true, 
                          locale: dateLocale 
                        })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">
                {t('dashboard.noActivity')}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}
